/**
 * The identity contract (D157) — what the identity Worker and everyone who
 * asks it agree on: where it answers, what its cookie is called, and the
 * one shape a "who is this" comes back as.
 *
 * @remarks
 * platform.md §5: the identity lives on `orpheusquest.com`, one across
 * every game. Better Auth is the implementation and its own routes are the
 * wire, so this file names the two facts other Workers depend on rather
 * than re-describing the whole protocol: the base path every Better Auth
 * route hangs off, and the session cookie's name — `play-api` looks for it
 * before it spends a subrequest asking, and the identity Worker configures
 * Better Auth from the same constant so the two can never disagree.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

/** The path every identity route hangs off — Better Auth's default, named once. */
export const IDENTITY_BASE_PATH = '/api/auth';

/** The route that answers "who is this" for the cookie it is sent — JSON, {@link identitySessionSchema}. */
export const IDENTITY_SESSION_PATH = `${IDENTITY_BASE_PATH}/get-session`;

/**
 * How long a magic link lives, in minutes — the one fact about a link that
 * BOTH ends tell the player: the Worker mints it with this lifetime and
 * says so in the email, the shell says so when a link comes back dead.
 * Named here so the product cannot tell a player two things about one
 * link (Calder's finding, 2026-09-08: it did, once).
 */
export const MAGIC_LINK_LIFETIME_MINUTES = 15;

/** Better Auth's cookie prefix here: the session cookie is `orpheus.session_token` (`__Secure-` in front when secure). */
export const IDENTITY_COOKIE_PREFIX = 'orpheus';

/** Whether a `Cookie` header carries the identity session cookie at all — the cheap check before the subrequest. */
export const carriesIdentityCookie = (cookieHeader: string | null): boolean =>
	cookieHeader !== null && cookieHeader.includes(`${IDENTITY_COOKIE_PREFIX}.session_token=`);

/*
 * ── The handoff (D170) ──
 *
 * The cookie is per site, and a creator's own domain is another site: a
 * page on `play.theirgame.com` cannot send it. platform.md §5 names the
 * fix — the page gets a short-lived token from `orpheusquest.com` — and
 * these are its facts. The player's Sign in on a foreign hostname sends
 * them to a home host with the return address in the query; the home host
 * (where the cookie IS first-party) signs them in if needed, mints a
 * one-time token, checks the return hostname against the host records,
 * and sends them back with the token in the FRAGMENT — never the query, so
 * it reaches no server log and no `Referer`; the foreign page strips it
 * before doing anything else, exchanges it once for a bearer session, and
 * from then on sends `Authorization: Bearer …` where a home page sends the
 * cookie. Both ends read these constants, so neither can drift.
 */

/** `GET`, with the cookie: mints a one-time token for the signed-in player. */
export const IDENTITY_HANDOFF_MINT_PATH = `${IDENTITY_BASE_PATH}/one-time-token/generate`;

/** What the mint door answers. */
export const handoffMintAnswerSchema = z.object({ token: z.string().min(1) }).readonly();

export type HandoffMintAnswer = z.infer<typeof handoffMintAnswerSchema>;

/** `POST { token }`, from anywhere: exchanges a token, once, for the session it was minted from. */
export const IDENTITY_HANDOFF_VERIFY_PATH = `${IDENTITY_BASE_PATH}/one-time-token/verify`;

/** How long a handoff token lives, in minutes — a redirect and back, with room for a slow network, and no more. */
export const HANDOFF_TOKEN_LIFETIME_MINUTES = 3;

/** The query parameter a foreign host sends a player to the home host with: the address to return them to. */
export const HANDOFF_RETURN_PARAM = 'handoff';

/** The fragment parameter the home host returns the player with: the one-time token. */
export const HANDOFF_TOKEN_PARAM = 'handoff';

/** The response header the identity Worker hands a bearer session back in — Better Auth's bearer plugin's name. */
export const BEARER_SESSION_HEADER = 'set-auth-token';

/** Whether an `Authorization` header carries a bearer session — the cheap check before the subrequest, the cookie's twin. */
export const carriesBearerSession = (authorizationHeader: string | null): boolean =>
	authorizationHeader !== null && /^bearer\s+\S/iu.test(authorizationHeader);

/*
 * ── The devices and the sign-in log (D171) ──
 *
 * platform.md §5 promised a device list with revoke and an audit log of
 * sign-ins. A device is a Better Auth session row (one per sign-in; the
 * handoff of D170 SHARES a session rather than making one, so a creator's
 * domain and the home site appear as one device, and revoking it signs
 * both out); a sign-in event is a row this Worker writes when a session is
 * created — kept until the account is deleted (Mike's ruling, 2026-09-11).
 */

/**
 * D173: the doors this deployment offers. A social provider without
 * credentials is not configured, and the library answers "Provider not
 * found" to a button the page should never have drawn; a magic link
 * without a mailer is a server error. The page asks once and draws only
 * what is offered — the passkey is always offered.
 */

/** `GET`, public: which doors are offered here. */
export const IDENTITY_DOORS_PATH = `${IDENTITY_BASE_PATH}/doors`;

/** The two social doors, in the order the dialog draws them. */
export const SOCIAL_PROVIDERS = ['google', 'apple'] as const;

export const socialProviderSchema = z.enum(SOCIAL_PROVIDERS);

export type SocialProvider = z.infer<typeof socialProviderSchema>;

/** What the doors door answers. */
export const doorsAnswerSchema = z
	.object({
		/** Whether a mailer is configured, so a sign-in link can be sent. */
		magicLink: z.boolean(),
		/** The social providers with credentials, in the dialog's order. */
		social: z.array(socialProviderSchema).readonly(),
	})
	.readonly();

export type DoorsAnswer = z.infer<typeof doorsAnswerSchema>;

/** `GET`, signed in: every session of the account, this one first. */
export const IDENTITY_DEVICES_PATH = `${IDENTITY_BASE_PATH}/devices`;

/** `POST { id }`, signed in: end one of the account's sessions — this one included. */
export const IDENTITY_DEVICE_REVOKE_PATH = `${IDENTITY_BASE_PATH}/devices/revoke`;

/** `POST`, signed in: end every session of the account but this one. */
export const IDENTITY_DEVICES_REVOKE_OTHERS_PATH = `${IDENTITY_BASE_PATH}/devices/revoke-others`;

/** `GET`, signed in: the account's sign-ins, newest first. */
export const IDENTITY_SIGN_INS_PATH = `${IDENTITY_BASE_PATH}/sign-ins`;

/** How many sign-ins the log answers at once — the newest; the rest are kept, not shown. */
export const SIGN_IN_LOG_LIMIT = 50;

/** The doors a session can be created through, as the log names them. */
export const SIGN_IN_DOORS = ['apple', 'google', 'magic-link', 'passkey', 'other'] as const;

export const signInDoorSchema = z.enum(SIGN_IN_DOORS);

export type SignInDoor = z.infer<typeof signInDoorSchema>;

/** One session of the account, as the device list shows it. */
export const deviceSessionSchema = z
	.object({
		/** When the session was created — the sign-in. */
		createdAtIso: z.string(),
		expiresAtIso: z.string(),
		/** The session's id — what the revoke door takes; never the token. */
		id: z.string().min(1),
		/** The connecting address at sign-in, when the edge supplied one. */
		ipAddress: z.string().optional(),
		/** Whether this is the session asking. */
		isCurrent: z.boolean(),
		/** When the session was last refreshed — the last visit, to the day. */
		lastSeenAtIso: z.string(),
		/** The browser's `User-Agent` at sign-in, as sent; the shell names the browser and OS from it. */
		userAgent: z.string().optional(),
	})
	.readonly();

export type DeviceSession = z.infer<typeof deviceSessionSchema>;

/** One sign-in, as the log shows it. */
export const signInEventSchema = z
	.object({
		/** The city Cloudflare attached to the request, when it did. */
		city: z.string().optional(),
		/** The country, as a two-letter code, when Cloudflare attached one. */
		country: z.string().optional(),
		createdAtIso: z.string(),
		door: signInDoorSchema,
		id: z.string().min(1),
		ipAddress: z.string().optional(),
		/** The session the sign-in made — matches a device's `id` while that session lives. */
		sessionId: z.string(),
		userAgent: z.string().optional(),
	})
	.readonly();

export type SignInEvent = z.infer<typeof signInEventSchema>;

/** What the devices door answers. */
export const devicesAnswerSchema = z.object({ devices: z.array(deviceSessionSchema).readonly() }).readonly();

export type DevicesAnswer = z.infer<typeof devicesAnswerSchema>;

/** What the sign-ins door answers. */
export const signInsAnswerSchema = z.object({ signIns: z.array(signInEventSchema).readonly() }).readonly();

export type SignInsAnswer = z.infer<typeof signInsAnswerSchema>;

/** What the revoke door takes: the device's id from the list. */
export const revokeDeviceRequestSchema = z.object({ id: z.string().min(1) });

export type RevokeDeviceRequest = z.infer<typeof revokeDeviceRequestSchema>;

/** What a revoke door answers: how many sessions ended. */
export const revokeAnswerSchema = z.object({ revoked: z.int().nonnegative() }).readonly();

export type RevokeAnswer = z.infer<typeof revokeAnswerSchema>;

/** The signed-in player, as everyone outside the identity Worker sees them. */
export const signedInUserSchema = z
	.object({
		email: z.string(),
		id: z.string().min(1),
		name: z.string(),
	})
	.readonly();

export type SignedInUser = z.infer<typeof signedInUserSchema>;

/**
 * What {@link IDENTITY_SESSION_PATH} answers: the user and their session, or
 * `null` when the cookie is absent, expired or revoked. Only the fields the
 * rest of the platform reads are named; Better Auth's others pass through
 * unread.
 */
export const identitySessionSchema = z
	.object({
		session: z.object({ expiresAt: z.string() }).readonly(),
		user: signedInUserSchema,
	})
	.readonly()
	.nullable();

export type IdentitySession = z.infer<typeof identitySessionSchema>;
